import React, { useState, useContext, useEffect } from 'react';

import { AuthContext } from '../authentication/AuthContext';

import { useEntityContext } from '../entities/EntityContext';
import {
  fetchAllFeatureFlags,
  createFeatureFlag as _createFeatureFlag,
  updateFeatureFlag as _updateFeatureFlag,
} from './featureFlagService';

const DEFAULT_FEATURE_FLAG_STATE = {
  isLoaded: false,
  findFeatureFlagByCode(code) {
    void code;
  },
  updateFeatureFlag(data) {
    void data;
  },
  createFeatureFlag(data) {
    void data;
  },
};

const FeatureFlagsContext = React.createContext(DEFAULT_FEATURE_FLAG_STATE);

export function FeatureFlagsProvider({ children }) {
  const { appendEntities } = useEntityContext();
  const [featureFlagsMap, setFeatureFlagsMap] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const { state: authState, setFeatureFlags } = useContext(AuthContext);

  async function fetchData() {
    if (isLoaded) {
      return;
    }

    await fetchAllFeatureFlags()
      .then(({ data }) => {
        const d = getFeatureFlagsMap(data);
        setFeatureFlagsMap(d);
        setFeatureFlags(d);
        setIsLoaded(true);
      })
      .catch((err) => {
        // eslint-disable-next-line no-alert
        alert('There was an error fetching feature flags');
        throw err;
      });
  }

  useEffect(() => {
    if (authState) {
      fetchData();
    }
  }, [authState]);

  useEffect(() => {
    if (!authState || !isLoaded) {
      return;
    }

    const globalFeatureFlags = Object.values(featureFlagsMap);

    appendEntities({
      entityName: 'featureFlag',
      entityKey: 'id',
      entities: globalFeatureFlags,
    });
  }, [isLoaded]);

  async function updateFeatureFlag({ code, description, isActive }) {
    await _updateFeatureFlag({ code, description, isActive });
    await fetchData();
  }

  async function createFeatureFlag({ data }) {
    const res = await _createFeatureFlag({ data });
    await fetchData();
    return res.data;
  }

  return (
    <FeatureFlagsContext.Provider
      value={{
        isLoaded,
        featureFlagsMap,
        updateFeatureFlag,
        createFeatureFlag,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
}

function getFeatureFlagsMap(featureFlags = []) {
  return featureFlags.reduce((acc, { code, isActive }) => {
    acc[code] = isActive;
    return acc;
  }, {});
}

export function useFeatureFlags() {
  const { isLoaded, featureFlagsMap, updateFeatureFlag, createFeatureFlag } =
    useContext(FeatureFlagsContext);

  function findFeatureFlagByCode(code) {
    return featureFlagsMap[code];
  }

  function getAllFeatureFlags() {
    return Object.values(featureFlagsMap);
  }

  return {
    isLoaded,
    findFeatureFlagByCode,
    updateFeatureFlag,
    createFeatureFlag,
    getAllFeatureFlags,
  };
}
